// src/pages/admin/components/AssignmentManagement/AssignmentManagement.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AssignmentList from './AssignmentList';
import AssignmentDetails from './AssignmentDetails';
import assignmentService from '../../services/assignmentService';
import './AssignmentManagement.css';
import '../../../../styles/admin.css';

// When rendered inside ProgramManagement, `program` is the selected program object.
// All assignments fetched and created are scoped to program.id.
const AssignmentManagement = ({ groups = [], program }) => {
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formTitle, setFormTitle] = useState('');
  const [formInstructions, setFormInstructions] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAssignments = useCallback(async () => {
    try {
      setLoading(true);
      const all = await assignmentService.fetchAssignments();
      const scoped = program
        ? all.filter(a => a.programId === program.id)
        : all;
      setAssignments(scoped);

      if (selectedAssignment) {
        const refreshed = scoped.find(a => a.id === selectedAssignment.id);
        if (refreshed) setSelectedAssignment(refreshed);
      }
    } catch (err) {
      setError("Failed to load assignments: " + err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program?.id]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  useEffect(() => {
    let timer;
    if (success || error) {
      timer = setTimeout(() => { setSuccess(null); setError(null); }, 5000);
    }
    return () => clearTimeout(timer);
  }, [success, error]);

  useEffect(() => {
    if (selectedAssignment) {
      setFormTitle(selectedAssignment.title || '');
      setFormInstructions(selectedAssignment.instruction || '');
    } else {
      setFormTitle('');
      setFormInstructions('');
    }
  }, [selectedAssignment]);

  const handleToggleEditing = useCallback(() => setIsEditing(prev => !prev), []);

  const handleSaveAssignment = useCallback(async () => {
    if (!selectedAssignment) return;
    try {
      setLoading(true);
      await assignmentService.saveAssignment({
        ...selectedAssignment,
        title: formTitle,
        instruction: formInstructions,
        ...(program ? { programId: program.id } : {})
      }, selectedAssignment.id);
      setSuccess("Assignment saved successfully");
      setIsEditing(false);
      fetchAssignments();
    } catch (err) {
      setError("Failed to save assignment: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedAssignment, formTitle, formInstructions, program, fetchAssignments]);

  const handleDeleteAssignment = useCallback(async () => {
    if (!selectedAssignment?.id) return;
    if (!window.confirm(`Are you sure you want to delete "${selectedAssignment.title}"?`)) return;
    try {
      setLoading(true);
      await assignmentService.deleteAssignment(selectedAssignment.id);
      setSuccess(`Assignment "${selectedAssignment.title}" deleted successfully`);
      setSelectedAssignment(null);
      await fetchAssignments();
    } catch (err) {
      setError(`Failed to delete assignment: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedAssignment, fetchAssignments]);

  const handleSelectAssignment = useCallback((assignment) => {
    setSelectedAssignment(assignment);
    setIsEditing(false);
  }, []);

  const handleCreateAssignment = useCallback(() => {
    setSelectedAssignment({
      id: null,
      title: "New Assignment",
      instruction: "",
      startDate: null,
      endDate: null,
      order: assignments.length,
      ...(program ? { programId: program.id } : {})
    });
    setIsEditing(true);
  }, [assignments.length, program]);

  const handleReorderAssignment = useCallback(async (assignmentId, newOrder) => {
    try {
      setLoading(true);
      const a = assignments.find(a => a.id === assignmentId);
      if (!a) throw new Error("Assignment not found");
      await assignmentService.saveAssignment({ ...a, order: newOrder }, assignmentId);
      fetchAssignments();
    } catch (err) {
      setError(`Failed to reorder assignment: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [assignments, fetchAssignments]);

  const filteredAssignments = useMemo(() => {
    return assignments
      .filter(a => {
        if (!searchTerm) return true;
        const lower = searchTerm.toLowerCase();
        return a.title.toLowerCase().includes(lower) || a.instruction?.toLowerCase().includes(lower);
      })
      .sort((a, b) => a.order - b.order);
  }, [assignments, searchTerm]);

  return (
    <div className="assignment-management-container" style={{ paddingTop: 0 }}>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="filter-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search assignments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>✕</button>
          )}
        </div>
      </div>

      <div className="assignment-management-content">
        <div className="left-panel">
          <AssignmentList
            assignments={filteredAssignments}
            selectedAssignment={selectedAssignment}
            onSelectAssignment={handleSelectAssignment}
            onCreateNew={handleCreateAssignment}
            getAssignmentStatus={assignmentService.getAssignmentStatus}
            onReorderAssignment={handleReorderAssignment}
          />
        </div>

        <div className="right-panel">
          {selectedAssignment ? (
            <AssignmentDetails
              key={`assignment-${selectedAssignment.id}-edit-${isEditing}`}
              selectedAssignment={selectedAssignment}
              formTitle={formTitle}
              setFormTitle={setFormTitle}
              formInstructions={formInstructions}
              setFormInstructions={setFormInstructions}
              onSave={handleSaveAssignment}
              onDelete={handleDeleteAssignment}
              onToggleEditing={handleToggleEditing}
              isEditing={isEditing}
              loading={loading}
            />
          ) : (
            <div className="no-selection-message">
              <p>Select an assignment from the list or create a new one.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentManagement;